import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaClock,
  FaUsers,
  FaVideo,
  FaSearch,
  FaFilter,
  FaArrowRight,
  FaBolt,
  FaBroadcastTower,
  FaThLarge,
  FaExclamationTriangle
} from "react-icons/fa";
import { getApiUrl } from "../../utils/api";
import "./Events.css";

/**
 * Custom Vector Illustration for Empty Events State
 * Matches the reference image calendar + cloud + sparkle stars + megaphone badge
 */
const EmptyCalendarIllustration = () => (
  <svg
    className="empty-calendar-svg"
    viewBox="0 0 200 150"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Floating Cloud Behind Calendar */}
    <path
      d="M48 108 C36 108 26 99 26 86 C26 74 34 65 46 63 C49 46 64 34 82 34 C97 34 110 43 116 56 C124 48 138 48 147 56 C156 52 168 57 172 68 C183 71 190 81 190 92 C190 104 180 108 168 108 Z"
      className="empty-cloud-path"
    />

    {/* Sparkle 4-point stars */}
    {/* Top Left Sparkle */}
    <path d="M42 48 Q47 48 47 43 Q47 48 52 48 Q47 48 47 53 Q47 48 42 48 Z" fill="#38bdf8" />
    {/* Far Left Sparkle */}
    <path d="M24 74 Q27 74 27 71 Q27 74 30 74 Q27 74 27 77 Q27 74 24 74 Z" fill="#0ea5e9" />
    {/* Top Right Sparkle */}
    <path d="M164 44 Q169 44 169 39 Q169 44 174 44 Q169 44 169 49 Q169 44 164 44 Z" fill="#38bdf8" />
    {/* Mid Right Sparkle */}
    <path d="M178 66 Q181 66 181 63 Q181 66 184 66 Q181 66 181 69 Q181 66 178 66 Z" fill="#818cf8" />

    {/* Calendar Base Drop Shadow */}
    <rect x="64" y="44" width="76" height="66" rx="14" className="empty-cal-shadow" />

    {/* Calendar White/Dark Body Card */}
    <rect x="62" y="40" width="76" height="66" rx="12" className="empty-cal-body" />

    {/* Calendar Top Blue Header Bar */}
    <path
      d="M62 52 C62 45.3726 67.3726 40 74 40 H126 C132.627 40 138 45.3726 138 52 V56 H62 V52 Z"
      fill="#3b82f6"
    />

    {/* Calendar Binder Loops */}
    <rect x="76" y="34" width="6" height="12" rx="3" fill="#60a5fa" />
    <rect x="118" y="34" width="6" height="12" rx="3" fill="#60a5fa" />

    {/* Calendar Date Blocks (Row 1) */}
    <rect x="72" y="65" width="8" height="8" rx="2" className="empty-cal-block" />
    <rect x="86" y="65" width="8" height="8" rx="2" className="empty-cal-block" />
    <rect x="100" y="65" width="8" height="8" rx="2" className="empty-cal-block" />
    <rect x="114" y="65" width="8" height="8" rx="2" className="empty-cal-block" />

    {/* Calendar Date Blocks (Row 2) */}
    <rect x="72" y="80" width="8" height="8" rx="2" className="empty-cal-block" />
    <rect x="86" y="80" width="8" height="8" rx="2" className="empty-cal-block" />
    <rect x="100" y="80" width="8" height="8" rx="2" className="empty-cal-block" />

    {/* Circular Megaphone Badge at Bottom Right of Calendar */}
    <circle cx="128" cy="96" r="14" fill="#8b5cf6" />
    <path
      d="M122 97 V95 C122 94.4 122.4 94 123 94 H125 L129 91 V101 L125 98 H123 C122.4 98 122 97.6 122 97 Z M131 93.5 C132 94.5 132.5 95.7 132.5 96 C132.5 96.3 132 97.5 131 98.5 M125 98 V101 H123 V98"
      stroke="#ffffff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const Events = () => {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    totalEvents: 0,
    totalRegistrations: 0,
    upcomingEvents: 0,
    liveEvents: 0,
    pastEvents: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters state
  const [activeFilter, setActiveFilter] = useState("all"); // 'all' | 'upcoming' | 'live' | 'past'
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = new URL(getApiUrl("/events"));
      url.searchParams.set("filter", activeFilter);
      if (selectedCategory !== "all") {
        url.searchParams.set("category", selectedCategory);
      }
      if (searchQuery.trim()) {
        url.searchParams.set("search", searchQuery.trim());
      }

      const res = await fetch(url.toString());
      const data = await res.json();

      if (data.success) {
        setEvents(data.events || []);
        if (data.stats) {
          setStats(data.stats);
        }
      } else {
        setError(data.message || "Failed to load events");
      }
    } catch (err) {
      console.error("❌ Events Fetch Error:", err);
      setError("Network error connecting to events service. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [activeFilter, selectedCategory, searchQuery]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchEvents();
  };

  // Derive unique categories from fetched events
  const categories = useMemo(() => {
    const set = new Set();
    events.forEach((ev) => {
      if (ev.category) set.add(ev.category);
    });
    return ["all", ...Array.from(set)];
  }, [events]);

  return (
    <div className="events-page-wrapper">
      {/* Decorative Atmosphere Glows */}
      <div className="events-ambient-glow glow-left" aria-hidden="true" />
      <div className="events-ambient-glow glow-right" aria-hidden="true" />

      <div className="events-page-container">
        {/* ================================================================= */}
        {/* HERO SECTION                                                      */}
        {/* ================================================================= */}
        <section className="events-hero-section">
          {/* Badge */}
          <div className="events-hero-badge">
            <FaBolt className="badge-icon" />
            <span>KNOWLEDGE & MASTERCLASS SESSIONS</span>
          </div>

          {/* Heading with styled gradient highlight & wave */}
          <h1 className="events-hero-title">
            Events &{" "}
            <span className="gradient-highlight">
              Sessions
              <svg
                className="heading-wave-accent"
                viewBox="0 0 160 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M3 8 C38 2, 72 13, 108 5 C128 1, 146 7, 157 8"
                  stroke="url(#heroWaveGrad)"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="heroWaveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#00c6ff" />
                    <stop offset="100%" stopColor="#0072ff" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h1>

          {/* Subtitle description */}
          <p className="events-hero-subtitle">
            Join hands-on tech webinars, coding masterclasses, and live architecture deep-dives. Learn directly,
            interact live on Google Meet or Zoom, and level up your engineering skills.
          </p>

          {/* =============================================================== */}
          {/* 3 INDIVIDUAL STATISTICS CARDS                                   */}
          {/* =============================================================== */}
          <div className="events-stats-cards-grid">
            {/* Stat Card 1: Total Events */}
            <div className="event-stat-card card-cyan">
              <div className="stat-card-icon-box cyan-icon">
                <FaCalendarAlt />
              </div>
              <div className="stat-card-info">
                <span className="stat-card-number">{stats.totalEvents}</span>
                <span className="stat-card-label">Total Events</span>
              </div>
            </div>

            {/* Stat Card 2: Total Registrations */}
            <div className="event-stat-card card-purple">
              <div className="stat-card-icon-box purple-icon">
                <FaUsers />
              </div>
              <div className="stat-card-info">
                <span className="stat-card-number">{stats.totalRegistrations}</span>
                <span className="stat-card-label">Total Registrations</span>
              </div>
            </div>

            {/* Stat Card 3: Upcoming Sessions */}
            <div className="event-stat-card card-emerald">
              <div className="stat-card-icon-box emerald-icon">
                <FaBroadcastTower />
              </div>
              <div className="stat-card-info">
                <span className="stat-card-number">{stats.upcomingEvents}</span>
                <span className="stat-card-label">Upcoming Sessions</span>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================= */}
        {/* FILTER & SEARCH CONTROLS                                          */}
        {/* ================================================================= */}
        <section className="events-controls-section">
          {/* Rounded Pill Filter Tabs */}
          <div className="events-filter-tabs">
            {[
              { key: "all", label: "All Events", icon: <FaThLarge /> },
              { key: "upcoming", label: "Upcoming Events", icon: <FaCalendarAlt /> },
              { key: "live", label: "Live Events", icon: <FaBroadcastTower /> },
              { key: "past", label: "Past Events", icon: <FaClock /> }
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`event-filter-pill ${activeFilter === tab.key ? "active" : ""}`}
                onClick={() => setActiveFilter(tab.key)}
              >
                <span className="filter-pill-icon">{tab.icon}</span>
                <span className="filter-pill-text">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Integrated Search Bar with Right Search Button */}
          <div className="events-search-bar-row">
            <form className="events-search-bar-form" onSubmit={handleSearchSubmit}>
              <FaSearch className="search-leading-icon" />
              <input
                type="text"
                placeholder="Search by title, tag, or topic..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="events-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => {
                    setSearchQuery("");
                    setTimeout(fetchEvents, 0);
                  }}
                  aria-label="Clear search query"
                >
                  ×
                </button>
              )}
              <button type="submit" className="events-search-submit-btn">
                <FaSearch className="btn-search-icon" />
                <span>Search</span>
              </button>
            </form>

            {/* Optional category selector if categories exist */}
            {categories.length > 2 && (
              <div className="events-category-filter-wrap">
                <FaFilter className="cat-filter-icon" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="events-category-dropdown"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat === "all" ? "All Categories" : cat}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </section>

        {/* ================================================================= */}
        {/* EVENTS CONTENT / EMPTY STATE / CARDS GRID                         */}
        {/* ================================================================= */}
        <section className="events-content-section">
          {loading ? (
            <div className="events-loading-state">
              <div className="events-spinner" />
              <p>Loading events & availability...</p>
            </div>
          ) : error ? (
            <div className="events-error-state">
              <FaExclamationTriangle className="error-icon" />
              <p>{error}</p>
              <button type="button" className="retry-btn" onClick={fetchEvents}>
                Retry
              </button>
            </div>
          ) : events.length === 0 ? (
            /* Empty State matching reference image */
            <div className="events-empty-card">
              <div className="empty-illustration-box">
                <EmptyCalendarIllustration />
              </div>
              <h3 className="empty-card-heading">No Events Found</h3>
              <p className="empty-card-subtext">
                {searchQuery || activeFilter !== "all" || selectedCategory !== "all"
                  ? "No published events match your filter criteria. Try resetting your search or filter."
                  : "No published events are currently scheduled. Please check back soon for upcoming sessions!"}
              </p>
              {(searchQuery || activeFilter !== "all" || selectedCategory !== "all") && (
                <button
                  type="button"
                  className="empty-reset-filters-btn"
                  onClick={() => {
                    setActiveFilter("all");
                    setSelectedCategory("all");
                    setSearchQuery("");
                  }}
                >
                  Reset All Filters
                </button>
              )}
            </div>
          ) : (
            /* Events Grid when events exist */
            <div className="events-cards-grid">
              {events.map((event) => {
                const total = event.totalSeats || 0;
                const booked = event.bookedSeats || 0;
                const remaining = Math.max(0, total - booked);
                const percentage = total > 0 ? Math.min(100, Math.round((booked / total) * 100)) : 0;
                const isFull = remaining === 0;

                return (
                  <article key={event._id || event.slug} className="event-item-card">
                    {/* Event Banner */}
                    <div className="event-banner-wrapper">
                      <img
                        src={event.bannerImage || event.eventBannerUrl || event.bannerImageUrl}
                        alt={event.title}
                        className="event-banner-image"
                        loading="lazy"
                      />
                      <div className="event-banner-badges">
                        <span className="event-cat-pill">{event.category}</span>
                        {isFull ? (
                          <span className="event-status-pill fully-booked">Fully Booked</span>
                        ) : (
                          <span className="event-status-pill open">Registration Open</span>
                        )}
                      </div>
                    </div>

                    {/* Event Card Body */}
                    <div className="event-card-body">
                      <h2 className="event-title">{event.title}</h2>
                      <p className="event-short-desc">{event.shortDescription}</p>

                      {/* Meta Rows */}
                      <div className="event-meta-rows">
                        <div className="event-meta-item">
                          <FaCalendarAlt className="meta-row-icon" />
                          <span>{event.eventDate}</span>
                        </div>
                        <div className="event-meta-item">
                          <FaClock className="meta-row-icon" />
                          <span>
                            {event.startTime} - {event.endTime} IST ({event.durationMinutes} mins)
                          </span>
                        </div>
                        <div className="event-meta-item">
                          <FaVideo className="meta-row-icon" />
                          <span>{event.meetingPlatform || "Live Online"}</span>
                        </div>
                      </div>

                      {/* Seat Availability Bar */}
                      <div className="event-seat-tracker">
                        <div className="seat-tracker-top">
                          <span className="seat-tracker-title">
                            <FaUsers /> Seat Availability
                          </span>
                          <span className={`seat-tracker-count ${isFull ? "sold-out" : ""}`}>
                            {isFull ? (
                              <b>0 Seats Left</b>
                            ) : (
                              <>
                                <b>{remaining}</b> / {total} Remaining
                              </>
                            )}
                          </span>
                        </div>
                        <div className="seat-tracker-track">
                          <div
                            className={`seat-tracker-bar ${isFull ? "full" : ""}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>

                      {/* View Details Action */}
                      <div className="event-card-action">
                        <Link
                          to={`/events/eventdetail/${event.slug}`}
                          className="event-details-cta-btn"
                          aria-label={`View details for ${event.title}`}
                        >
                          <span>View Details</span>
                          <FaArrowRight className="cta-arrow-icon" />
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Events;
